// Content provider: loads products / articles / settings / landing / about / contact from R2 public URL (with caching + fallbacks).
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { loadJSON, saveJSON } from "./storage";
import {
  DEFAULT_PRODUCTS,
  DEFAULT_ARTICLES,
  DEFAULT_SETTINGS,
  DEFAULT_CATEGORIES,
  DEFAULT_LANDING,
  DEFAULT_ABOUT,
  DEFAULT_CONTACT,
} from "./seed";

const ContentContext = createContext(null);

export const PRODUCTS_KEY = "products.json";
export const ARTICLES_KEY = "articles.json";
export const SETTINGS_KEY = "settings.json";
export const LANDING_KEY = "landing.json";
export const ABOUT_KEY = "about.json";
export const CONTACT_KEY = "contact.json";
export const CATEGORIES_KEY = "categories.json";

export function ContentProvider({ children }) {
  const [products, setProducts] = useState(null);
  const [articles, setArticles] = useState(null);
  const [settings, setSettings] = useState(null);
  const [landing, setLanding] = useState(null);
  const [about, setAbout] = useState(null);
  const [contact, setContact] = useState(null);
  const [categories, setCategories] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    const [p, a, s, l, ab, ct, cat] = await Promise.all([
      loadJSON(PRODUCTS_KEY, DEFAULT_PRODUCTS),
      loadJSON(ARTICLES_KEY, DEFAULT_ARTICLES),
      loadJSON(SETTINGS_KEY, DEFAULT_SETTINGS),
      loadJSON(LANDING_KEY, DEFAULT_LANDING),
      loadJSON(ABOUT_KEY, DEFAULT_ABOUT),
      loadJSON(CONTACT_KEY, DEFAULT_CONTACT),
      loadJSON(CATEGORIES_KEY, DEFAULT_CATEGORIES),
    ]);
    setProducts(p || DEFAULT_PRODUCTS);
    setArticles(a || DEFAULT_ARTICLES);
    setSettings({ ...DEFAULT_SETTINGS, ...(s || {}) });
    setLanding({ ...DEFAULT_LANDING, ...(l || {}) });
    setAbout({ ...DEFAULT_ABOUT, ...(ab || {}) });
    setContact({ ...DEFAULT_CONTACT, ...(ct || {}) });
    setCategories(cat || DEFAULT_CATEGORIES);
    setLoaded(true);
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const saveProducts = async (r2, newList) => {
    setProducts(newList);
    await saveJSON(r2, PRODUCTS_KEY, newList);
  };
  const saveArticles = async (r2, newList) => {
    setArticles(newList);
    await saveJSON(r2, ARTICLES_KEY, newList);
  };
  const saveSettings = async (r2, next) => {
    setSettings(next);
    await saveJSON(r2, SETTINGS_KEY, next);
  };
  const saveLanding = async (r2, next) => {
    setLanding(next);
    await saveJSON(r2, LANDING_KEY, next);
  };
  const saveAbout = async (r2, next) => {
    setAbout(next);
    await saveJSON(r2, ABOUT_KEY, next);
  };
  const saveContact = async (r2, next) => {
    setContact(next);
    await saveJSON(r2, CONTACT_KEY, next);
  };
  const saveCategories = async (r2, next) => {
    setCategories(next);
    await saveJSON(r2, CATEGORIES_KEY, next);
  };

  return (
    <ContentContext.Provider
      value={{
        products: loaded ? (products ?? DEFAULT_PRODUCTS) : null,
        articles: loaded ? (articles ?? DEFAULT_ARTICLES) : null,
        settings: loaded ? (settings ?? DEFAULT_SETTINGS) : null,
        landing: loaded ? (landing ?? DEFAULT_LANDING) : null,
        about: loaded ? (about ?? DEFAULT_ABOUT) : null,
        contact: loaded ? (contact ?? DEFAULT_CONTACT) : null,
        categories: loaded ? (categories ?? DEFAULT_CATEGORIES) : null,
        loaded, reload,
        saveProducts, saveArticles, saveSettings, saveLanding, saveAbout, saveContact, saveCategories,
      }}
    >
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  return useContext(ContentContext);
}

export function persianSlug(text, fallback = "") {
  const s = (text || "").trim().toLowerCase();
  const out = s
    .replace(/\s+/g, "-")
    .replace(/[^\w\u0600-\u06FF-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return out || fallback;
}

export function uid() {
  return (Date.now().toString(36) + Math.random().toString(36).slice(2, 8)).toLowerCase();
}
